import { GetServerSidePropsContext } from 'next';
import RecommendedRetailersProvider from '@/providers/recommended-retailers/recommended-retailers.provider';
import ContentfulProvider from '@/providers/contentful';
import Partner from '@/models/partner';
import {
  IMAGE_POSITION,
  VersatileCardProperties,
} from '@/components/integrated/versatile-card/versatile-card.types';
import { VARIANT } from '@/components/integrated/partner-tile/partner-tile.types';
import { arrayToObject } from '@/utils/array-to-object';
import { relatedPartnersCarouselQuery } from './related-partners-carousel.query';
import {
  RelatedPartnersCarouselProperties,
  RelatedPartnersCarouselResponse,
} from './related-partners-carousel.types';

export const relatedPartnersCarouselHandler = async (
  { req: { opco, b2bToken } }: GetServerSidePropsContext,
  partnerSlug: string,
): Promise<RelatedPartnersCarouselProperties | null> => {
  const partner = new Partner(opco, partnerSlug);
  const contentfulRequest = new ContentfulProvider(
    opco,
  ).makeContentfulRequest<RelatedPartnersCarouselResponse>({
    query: relatedPartnersCarouselQuery,
  });

  const contentfulResponse = await contentfulRequest;
  const content = arrayToObject(
    contentfulResponse.data.microcopyCollection.items,
  );

  await partner.isReady();
  if (!partner.isValid || !partner.id) return null;

  const provider = new RecommendedRetailersProvider(opco, b2bToken);
  let retailersData = await provider.getRelatedRetailers(partner.id);
  if (!retailersData)
    retailersData = await provider.getRelatedRetailers(partner.id);

  let relatedPartners;
  if (retailersData && retailersData.length > 0) {
    relatedPartners = await Partner.findByConditions({
      opco,
      merchantIds: retailersData.map(Number),
    });

    await Promise.all(
      relatedPartners.map(relatedPartner => relatedPartner.isReady()),
    );
  }

  // if no related partners found, use old logic to fetch related retailers
  if (!relatedPartners || relatedPartners.filter(x => x.isValid).length === 0) {
    const relatedPartnersPromise = await partner.getRelatedPartners();
    [relatedPartners] = await Promise.all([relatedPartnersPromise]);
  }

  const partnerCategory = partner?.category;
  const formattedRelatedPartners = relatedPartners
    .filter(x => x.isValid)
    .map(relatedPartner => {
      const {
        collectionMethods,
        id,
        name,
        slug,
        logoSrc,
        heroSrc,
        mechanicId,
      } = relatedPartner;

      let mechanicHasSpeedy = null;
      let mechanicRate = null;
      for (const mechanic of Object.values(collectionMethods)) {
        const { rate, isSpeedyAwarding } = mechanic;
        mechanicHasSpeedy = isSpeedyAwarding;
        mechanicRate = rate;
      }
      return {
        name,
        content: {
          partner: {
            variant: VARIANT.Default,
            partnerId: id,
            mechanicId,
            logo: { src: logoSrc, alt: name },
            slug,
            name,
            isSpeedyAwarding: mechanicHasSpeedy,
            wasRate: null,
            rate: mechanicRate,
            speedyAwardText: content['speedyAward.title'],
          },
        },
        image: {
          alt: name,
          src: heroSrc,
          height: 125,
          mobileHeight: 125,
          position: IMAGE_POSITION.Top,
          optimised: null,
        },
      } as VersatileCardProperties;
    });

  return {
    relatedPartnersTitle: content['relatedPartners.title'],
    relatedPartners: formattedRelatedPartners,
    partnerCategory,
    seeAllText: content['merchant.seeall'],
    favouritesLabels: {
      addToFavourites: content['favourites.button.addToFavourites'] ?? null,
      removeFromFavourites:
        content['favourites.button.removeFromFavourites'] ?? null,
    },
  };
};
